<template>
    <div>
        <v-navigation-drawer
            permanent
            class="leftSidebar"
            :class="{
                mini: mini_sidebar,
                'leftSidebar--overlay-expand': isMobile && !mini_sidebar,
            }"
            width="300"
            rail-width="56"
            :scrim="false"
            :rail="isMobile || mini_sidebar"
            app
            @mouseenter="hoverExpand"
            @mouseleave="hoverCollapse"
        >
            <div class="pa-5 logo-section">
                <h3 class="logo-title">
                    {{ mini_sidebar ? "M" : $t("logo.title") }}
                </h3>
                <span v-if="!mini_sidebar" class="logo-subtitle">{{
                    $t("logo.subtitle")
                }}</span>
            </div>
            <div class="menu-container">
                <v-list aria-busy="true" aria-label="menu list">
                    <template v-for="(item, i) in sidebarMenu" :key="i">
                        <!-- Sub Headers -->
                        <NavGroup
                            v-if="item.header"
                            :item="item"
                            :mini="mini_sidebar"
                        />

                        <!-- Dividers -->
                        <v-divider v-else-if="item.divider" />

                        <!-- Nested Menus -->
                        <NavCollapse
                            v-else-if="item.children"
                            :item="item"
                            :level="0"
                            :mini="mini_sidebar"
                        />

                        <!-- Single Item -->
                        <NavItem v-else :item="item" :mini="mini_sidebar" />
                    </template>
                </v-list>
            </div>

            <footer v-if="!mini_sidebar" class="sidebar-footer">
                <div
                    v-if="sessionUserDisplayName"
                    class="sidebar-profile-block"
                    role="button"
                    tabindex="0"
                    @click="goToEditEmployee"
                    @keydown.enter.prevent="goToEditEmployee"
                >
                    <div class="sidebar-profile-name">
                        {{ sessionUserDisplayName }}
                    </div>
                    <div v-if="sessionCompanyName" class="sidebar-profile-company">
                        ({{ sessionCompanyName }})
                    </div>
                </div>
                <p>{{ $t("footer.copyright") }}</p>
                <p>{{ $t("footer.poweredBy") }}</p>
            </footer>
        </v-navigation-drawer>
    </div>
</template>

<script>
import { shallowRef, ref } from "vue";
import { mapState, mapMutations } from "vuex";
import emitter from "@/eventBus";
import sidebarItem from "./sidebarItem";
import NavCollapse from "./navCollapse/NavCollapse.vue";
import NavItem from "./navItem/NavItem.vue";
import NavGroup from "./navGroup/NavGroup.vue";
import LanguageSelector from "@/components/LanguageSelector.vue";
import { getChatUnreadCount, getReservationBadgeCount } from "@/api";

export default {
    components: {
        NavCollapse,
        NavItem,
        NavGroup,
        LanguageSelector,
    },
    data() {
        return {
            /**
             * Reactive reference to the sidebar menu items.
             * It updates dynamically based on the selected language.
             * @type {import('vue').ShallowRef<Array<Object>>}
             */
            sidebarMenu: shallowRef(sidebarItem(this.$t, [], {
                ware_received: 0,
                truck_received: 0,
                ware_accepted: 0,
                truck_accepted: 0,
                ware_chat: 0,
                truck_chat: 0,
                ware_entry_chat: 0,
                truck_entry_chat: 0,
            })),
            badgeCount: ref({
                ware_received: 0,
                truck_received: 0,
                ware_accepted: 0,
                truck_accepted: 0,
                ware_chat: 0,
                truck_chat: 0,
                ware_entry_chat: 0,
                truck_entry_chat: 0,
            }),
            /** True when full width came from hover-expand (desktop rail), so mouseleave can collapse back to rail. */
            railExpandedByHover: false,
        };
    },
    computed: {
        ...mapState(["mini_sidebar", "isMobile"]),
        sessionUserDisplayName() {
            try {
                const userJsonString = sessionStorage.getItem("user");
                if (!userJsonString) return "";
                const sessionUser = JSON.parse(userJsonString);
                return [sessionUser.first_name, sessionUser.last_name]
                    .map((namePart) =>
                        namePart == null ? "" : String(namePart).trim()
                    )
                    .filter(Boolean)
                    .join(" ");
            } catch {
                return "";
            }
        },
        sessionCompanyName() {
            try {
                const userJsonString = sessionStorage.getItem("user");
                if (!userJsonString) return "";
                const sessionUser = JSON.parse(userJsonString);
                const companyNameValue = sessionUser.company_name;
                return companyNameValue != null ? String(companyNameValue) : "";
            } catch {
                return "";
            }
        },
    },
    watch: {
        /**
         * Watches for language changes and reinitializes the sidebar menu.
         */
        "$i18n.locale"() {
            this.sidebarMenu = shallowRef(sidebarItem(this.$t, [], this.badgeCount));
        },
        badgeCount: {
            handler(newBadgeCounts) {
                this.sidebarMenu = shallowRef(sidebarItem(this.$t, [], newBadgeCounts));
            },
            deep: true,
        },
        "$route.path"(routePath) {
            if (routePath && !routePath.includes("reservations")) {
                this.fetchBadgeCount();
            }
        },
    },
    methods: {
        ...mapMutations(["SET_MINI_SIDEBAR"]),

        /**
         * Desktop: full width by default. Small screens: icon rail by default (see updateScreenSize).
         */
        initializeSidebar() {
            this.SET_MINI_SIDEBAR(!!this.isMobile);
        },

        /**
         * Toggles rail (icons-only) vs expanded drawer.
         * Below 1145px (isMobile): expand overlays content; layout stays on rail width.
         * Desktop: expand pushes content (rail prop turns off).
         */
        toggleSidebar() {
            this.railExpandedByHover = false;
            this.SET_MINI_SIDEBAR(!this.mini_sidebar);
        },

        /**
         * Expands the sidebar when hovered over in rail mode (desktop).
         */
        hoverExpand() {
            if (this.isMobile) return;
            if (this.mini_sidebar) {
                this.railExpandedByHover = true;
                this.SET_MINI_SIDEBAR(false);
            }
        },

        /**
         * Collapses back to rail only after hover-expand (not after menu toggle).
         */
        hoverCollapse() {
            if (this.isMobile) return;
            if (this.railExpandedByHover) {
                this.railExpandedByHover = false;
                this.SET_MINI_SIDEBAR(true);
            }
        },

        async fetchBadgeCount() {
            try {
                const [reservationBadgeCounts, chatUnread] = await Promise.all([
                    getReservationBadgeCount(),
                    getChatUnreadCount(),
                ]);
                this.badgeCount = {
                    ...reservationBadgeCounts,
                    ware_chat: chatUnread.ware_reservation || 0,
                    truck_chat: chatUnread.truck_reservation || 0,
                    ware_entry_chat: chatUnread.ware_entry || 0,
                    truck_entry_chat: chatUnread.truck_entry || 0,
                };
            } catch {
                this.badgeCount = {
                    ware_received: 0,
                    truck_received: 0,
                    ware_accepted: 0,
                    truck_accepted: 0,
                    ware_chat: 0,
                    truck_chat: 0,
                    ware_entry_chat: 0,
                    truck_entry_chat: 0,
                };
            }
        },
        handleReservationWsUpdate(websocketPayload) {
            if (
                websocketPayload.type === "reservation_update" &&
                websocketPayload.badge_affected
            ) {
                this.fetchBadgeCount();
            }
            if (websocketPayload.type === "chat_unread_affected") {
                this.fetchBadgeCount();
            }
        },
        goToEditEmployee() {
            let sessionUser;
            try {
                sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
            } catch {
                return;
            }
            const currentUserId = sessionUser?.id;
            if (
                currentUserId === undefined ||
                currentUserId === null ||
                currentUserId === ""
            ) {
                return;
            }
            this.$router.push({
                name: "AddEmployee",
                params: { id: String(currentUserId) },
            });
        },
    },
    async created() {
        this.initializeSidebar();
        this.sidebarMenu = shallowRef(sidebarItem(this.$t, [], this.badgeCount));
    },
    async mounted() {
        await this.fetchBadgeCount();
        emitter.on("refresh-reservation-badge", this.fetchBadgeCount);
        emitter.on("refresh-chat-unread", this.fetchBadgeCount);
        emitter.on("ws-update", this.handleReservationWsUpdate);
    },
    beforeUnmount() {
        emitter.off("refresh-reservation-badge", this.fetchBadgeCount);
        emitter.off("refresh-chat-unread", this.fetchBadgeCount);
        emitter.off("ws-update", this.handleReservationWsUpdate);
    },
};
</script>

<style scoped>

:deep(.v-list-group__items) {
  --indent-padding: 20px;
}

:deep(.v-list-item-title) {
  text-wrap: auto;
}

:deep(.leftSidebar.mini .v-list) {
    margin-left: 15px !important;
}

/* hide all list headers / subheaders completely */
:deep(.leftSidebar.mini .v-list-subheader) {
    display: none !important;
}

/* center icons, strip padding on every list item */
:deep(.leftSidebar.mini .v-list-item) {
    justify-content: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

/* hide every title text */
:deep(.leftSidebar.mini .v-list-item__content),
:deep(.leftSidebar.mini .v-list-item-title) {
    display: none !important;
}

/* collapse group toggles: remove the chevron / arrow */
:deep(.leftSidebar.mini .v-list-group__header .v-list-item__append) {
    display: none !important;
}

/* hide the footer entirely */
:deep(.leftSidebar.mini .sidebar-footer) {
    display: none !important;
}
</style>

